/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import {ColumnFiltersState, SortingState, VisibilityState} from '@tanstack/react-table'
import {Offer} from 'types/entities/Offer'
import type {OfferCalculation} from 'types/entities/OfferCalculation'
import {EmailTemplate} from 'types/entities/EmailTemplate'
import {OfferNote} from 'types/entities/OfferNote'
import type {PackagesCart} from "types/entities/PackagesCart";
import {ReservationStatus} from 'types/enums/ReservationStatus'

const isActiveReservationLink = (reservation?: Offer['reservation'] | null): boolean => {
    if (!reservation?.id) {
        return false
    }

    const status = String(reservation.status || '').toLowerCase()
    return status !== ReservationStatus.Deleted && status !== ReservationStatus.Cancelled
}

type TableSearchScalar = string | number | boolean | null
type TableSearch =
    | TableSearchScalar
    | TableSearchScalar[]
    | Record<string, TableSearchScalar | TableSearchScalar[]>

const assignMergedPackagesCart = (
    previous: PackagesCart | null | undefined,
    packagesCart: PackagesCart
): PackagesCart => ({
    ...packagesCart,
    // Item mutation endpoints often rehydrate a stale nested reservation.
    // Preserve the cart reservation already in state (including explicit null after clear).
    reservation: previous ? previous.reservation ?? null : packagesCart.reservation ?? null,
})

const mergePackagesCartIntoOffer = (offer: Offer, packagesCart: PackagesCart): boolean => {
    let updated = false

    if (
        offer.packagesCart?.id === packagesCart.id ||
        offer.packages_cart_id === packagesCart.id
    ) {
        offer.packagesCart = assignMergedPackagesCart(offer.packagesCart, packagesCart)
        offer.packages_cart_id = packagesCart.id
        updated = true
    }

    if (offer.calculations?.length) {
        offer.calculations.forEach((calculation) => {
            if (
                calculation.packagesCart?.id === packagesCart.id ||
                calculation.packages_cart_id === packagesCart.id
            ) {
                calculation.packagesCart = assignMergedPackagesCart(
                    calculation.packagesCart,
                    packagesCart
                )
                updated = true
            }
        })
    }

    return updated
}

interface OffersTableUiState {
    pageIndex: number
    pageSize: number
    globalFilter: string
    columnFilters: ColumnFiltersState
    sorting: SortingState
    view: string | 'all'
    columnVisibility?: VisibilityState
}

export const DEFAULT_OFFERS_TABLE_UI: OffersTableUiState = {
    pageIndex: 0,
    pageSize: 50,
    globalFilter: '',
    columnFilters: [],
    sorting: [{ id: 'created_at', desc: true }],
    view: 'all',
    columnVisibility: {
        company_branche: false,
        company_state: false,
        company_address: false,
        contact_position: false,
        contact_gender: false,
    },
}

interface OffersTableMetaState {
    currentPage: number
    perPage: number
    total: number
    sortBy: string | null
    sort: 'asc' | 'desc' | null
    search: TableSearch
}

interface OffersTableState {
    ui: OffersTableUiState
    meta: OffersTableMetaState | null
    hasLoaded: boolean
}

export interface OffersState {
    all: Offer[]
    selectedOffer: Offer | null
    draftOfferId: number | null
    emailTemplates: EmailTemplate[]
    table: OffersTableState
}

const initialState: OffersState = {
    all: [],
    selectedOffer: null,
    draftOfferId: null,
    emailTemplates: [],
    table: {
        ui: {
            ...DEFAULT_OFFERS_TABLE_UI,
        },
        meta: null,
        hasLoaded: false,
    },
}

const offersSlice = createSlice({
    name: 'offers',
    initialState,
    reducers: {
        setOffers(state, action: PayloadAction<Offer[]>) {
            state.all = action.payload ?? []
        },
        pushOffer(state, action: PayloadAction<Offer>) {
            const offer = action.payload
            const index = state.all.findIndex((o) => o.id === offer.id)
            if (index === -1) {
                state.all.push(offer)
            } else {
                state.all[index] = { ...state.all[index], ...offer }
            }

            if (state.selectedOffer?.id === offer.id) {
                state.selectedOffer = { ...state.selectedOffer, ...offer }
            }
        },
        pushCalculationToOffer(state, action: PayloadAction<OfferCalculation>) {
            const offerCalculation = action.payload
            const offerIndex = state.all.findIndex(
                (o) => o.id === offerCalculation.offer_id
            )
            const selectedOffer = state.selectedOffer
            if (!selectedOffer) {
                return
            }
            if (!selectedOffer.calculations) {
                selectedOffer.calculations = []
            }
            if (offerIndex !== -1 && !state.all[offerIndex].calculations) {
                state.all[offerIndex].calculations = []
            }

            const calcIndex = selectedOffer.calculations.findIndex(
                (c) => c.id === offerCalculation.id
            )

            const calculationToAdd = {
                ...offerCalculation,
                is_target: offerCalculation.is_target ?? false,
            }

            if (calcIndex === -1) {
                selectedOffer.calculations.push(calculationToAdd)
                if (offerIndex !== -1) {
                    state.all[offerIndex].calculations?.push(calculationToAdd)
                }
            } else {
                selectedOffer.calculations[calcIndex] = calculationToAdd
                if (offerIndex !== -1 && state.all[offerIndex].calculations) {
                    state.all[offerIndex].calculations[calcIndex] = calculationToAdd
                }
            }
        },
        deleteOffer(state, action: PayloadAction<Offer['id']>) {
            const id = action.payload
            state.all = state.all.filter((o) => o.id !== id)
            if (state.selectedOffer?.id === id) {
                state.selectedOffer = null
            }
        },
        setSelectedOffer(state, action: PayloadAction<Offer>) {
            const incomingOffer = action.payload
            if (
                state.selectedOffer &&
                state.selectedOffer.id === incomingOffer.id
            ) {
                const mergedOffer: Offer = {
                    ...state.selectedOffer,
                    ...incomingOffer,
                }

                if ('reservation' in incomingOffer) {
                    const incomingReservation = incomingOffer.reservation ?? null
                    // fetchOfferById often returns reservation: null even after Create Booking.
                    // Keep the local active link unless the API sends a concrete reservation
                    // update (or clearSelectedOfferReservation wiped it first).
                    if (isActiveReservationLink(incomingReservation)) {
                        mergedOffer.reservation = incomingReservation
                    } else if (!isActiveReservationLink(state.selectedOffer.reservation)) {
                        mergedOffer.reservation = incomingReservation
                    } else {
                        mergedOffer.reservation = state.selectedOffer.reservation
                    }
                }

                state.selectedOffer = mergedOffer
            } else {
                state.selectedOffer = incomingOffer
            }
        },
        clearSelectedOfferReservation(state) {
            const clearReservationFromOffer = (offer: Offer) => {
                offer.reservation = null

                if (offer.packagesCart) {
                    offer.packagesCart.reservation = null
                    offer.packagesCart.reservation_id = null
                }

                offer.calculations?.forEach((calculation) => {
                    if (calculation.packagesCart) {
                        calculation.packagesCart.reservation = null
                        calculation.packagesCart.reservation_id = null
                    }
                })
            }

            if (state.selectedOffer) {
                clearReservationFromOffer(state.selectedOffer)
            }

            const selectedOfferIndex = state.all.findIndex(
                (offer) => offer.id === state.selectedOffer?.id
            )
            if (selectedOfferIndex !== -1) {
                clearReservationFromOffer(state.all[selectedOfferIndex])
            }
        },
		setSelectedOfferPackagesCart(state, action: PayloadAction<PackagesCart>) {
            if (!state.selectedOffer) {
                return
            }

            mergePackagesCartIntoOffer(state.selectedOffer, action.payload)

            const index = state.all.findIndex((offer) => offer.id === state.selectedOffer?.id)
            if (index !== -1) {
                mergePackagesCartIntoOffer(state.all[index], action.payload)
            }
		},
		setSelectedOfferCalculationPackagesCart(state, action: PayloadAction<PackagesCart>) {
			if(state.selectedOffer) {
				const updateCalculationIndex = state.selectedOffer.calculations?.findIndex(calc => calc.packages_cart_id === action.payload.id)
				if(updateCalculationIndex === undefined || updateCalculationIndex === -1) {
					return;
				}
				const previousCart = state.selectedOffer.calculations[updateCalculationIndex].packagesCart
				state.selectedOffer.calculations[updateCalculationIndex].packagesCart =
					assignMergedPackagesCart(previousCart, action.payload)
			}
		},
        resetSelectedOffer(state) {
            state.selectedOffer = null
        },
        setDraftOfferId(state, action: PayloadAction<number | null>) {
            state.draftOfferId = action.payload
        },
        clearOffers(state) {
            state.all = []
            state.selectedOffer = null
            state.table = {
                ui: { ...initialState.table.ui },
                meta: null,
                hasLoaded: false,
            }
        },
        setOffersTableUiState(
            state,
            action: PayloadAction<Partial<OffersTableUiState>>
        ) {
            if (!state.table) {
                state.table = {
                    ui: { ...initialState.table.ui },
                    meta: null,
                    hasLoaded: false,
                }
            }
            if (!state.table.ui) {
                state.table.ui = { ...initialState.table.ui }
            }
            state.table.ui = {
                ...state.table.ui,
                ...action.payload,
            }
        },
        resetDefaultOffersTableValues(state) {
            if (!state.table) {
                state.table = {
                    ui: { ...initialState.table.ui },
                    meta: null,
                    hasLoaded: false,
                }
                return
            }

            state.table.ui = { ...initialState.table.ui }
        },
        setOffersTableMeta(state, action: PayloadAction<OffersTableMetaState>) {
            if (!state.table) {
                state.table = {
                    ui: { ...initialState.table.ui },
                    meta: null,
                    hasLoaded: false,
                }
            }
            state.table.meta = action.payload
            state.table.hasLoaded = true
        },
        setPackageCartToOffer(
            state,
            action: PayloadAction<Offer['packagesCart']>
        ) {
            if (!action.payload) {
                return
            }

            if (state.selectedOffer) {
                mergePackagesCartIntoOffer(state.selectedOffer, action.payload)
            }

            state.all.forEach((offer) => {
                mergePackagesCartIntoOffer(offer, action.payload as PackagesCart)
            })
        },
        mergePackagesCartResponse(
            state,
            action: PayloadAction<{ packagesCart: PackagesCart; offerId?: number }>
        ) {
            const { packagesCart, offerId } = action.payload

            if (state.selectedOffer) {
                const selectedMatchesOfferId = offerId
                    ? state.selectedOffer.id === offerId
                    : false
                const selectedMatchesCartId =
                    state.selectedOffer.packages_cart_id === packagesCart.id ||
                    state.selectedOffer.packagesCart?.id === packagesCart.id

                if (selectedMatchesOfferId || selectedMatchesCartId) {
                    mergePackagesCartIntoOffer(state.selectedOffer, packagesCart)
                }
            }

            state.all.forEach((offer) => {
                const matchesOfferId = offerId ? offer.id === offerId : false
                const matchesCartId =
                    offer.packages_cart_id === packagesCart.id ||
                    offer.packagesCart?.id === packagesCart.id ||
                    !!offer.calculations?.some(
                        (calculation) =>
                            calculation.packages_cart_id === packagesCart.id ||
                            calculation.packagesCart?.id === packagesCart.id
                    )

                if (matchesOfferId || matchesCartId) {
                    mergePackagesCartIntoOffer(offer, packagesCart)
                }
            })
        },
        pushOfferEmailTemplate(
            state,
            action: PayloadAction<EmailTemplate>
        ) {
            // Update the main list (used by OfferEmailTemplatesPage)
            const listIndex = state.emailTemplates.findIndex(
                (t) => t.id === action.payload.id
            )
            if (listIndex === -1) {
                state.emailTemplates.push(action.payload)
            } else {
                state.emailTemplates[listIndex] = action.payload
            }

            // Update the list inside selectedOffer if applicable
            if (!state.selectedOffer) return
            if (!state.selectedOffer.emailTemplates) {
                state.selectedOffer.emailTemplates = []
            }
            const index = state.selectedOffer.emailTemplates.findIndex(
                (t) => t.id === action.payload.id
            )
            if (index === -1) {
                state.selectedOffer.emailTemplates.push(action.payload)
            } else {
                state.selectedOffer.emailTemplates[index] = action.payload
            }
        },
        deleteOfferEmailTemplate(
            state,
            action: PayloadAction<EmailTemplate>
        ) {
            if (!state.selectedOffer || !state.selectedOffer.emailTemplates)
                return
            state.selectedOffer.emailTemplates =
                state.selectedOffer.emailTemplates.filter(
                    (t) => t.id !== action.payload.id
                )
        },
        setOfferEmailTemplates(
            state,
            action: PayloadAction<EmailTemplate[]>
        ) {
            state.emailTemplates = action.payload ?? []
        },
        pushOfferNote(state, action: PayloadAction<OfferNote>) {
            // Update the offer in state.all if present
            const offer = state.all.find(
                (o) => o.id === action.payload.offer_id
            )
            if (offer) {
                if (!offer.offerNotes) {
                    offer.offerNotes = []
                }
                const index = offer.offerNotes.findIndex(
                    (n) => Number(n.id) === Number(action.payload.id)
                )
                if (index === -1) {
                    offer.offerNotes.push(action.payload)
                } else {
                    offer.offerNotes[index] = action.payload
                }
            }

            // Always update selectedOffer regardless of whether offer is in state.all
            // (e.g. when offer is loaded via fetchOfferById from opportunity/lead view)
            if (state.selectedOffer && state.selectedOffer.id === action.payload.offer_id) {
                if (!state.selectedOffer.offerNotes) {
                    state.selectedOffer.offerNotes = []
                }
                const selectedIndex = state.selectedOffer.offerNotes.findIndex(
                    (n) => Number(n.id) === Number(action.payload.id)
                )
                if (selectedIndex === -1) {
                    state.selectedOffer.offerNotes.push(action.payload)
                } else {
                    state.selectedOffer.offerNotes[selectedIndex] =
                        action.payload
                }
            }
        },
        deleteOfferNote(
            state,
            action: PayloadAction<{ offer_id: number; note_id: number }>
        ) {
            const noteIdsToRemove = (notes: OfferNote[] | undefined, rootNoteId: number) => {
                if (!notes?.length) {
                    return new Set<number>()
                }

                const ids = new Set<number>([rootNoteId])
                let foundNew = true

                while (foundNew) {
                    foundNew = false
                    notes.forEach((note) => {
                        const parentId =
                            note.replied_note_id === null || note.replied_note_id === undefined
                                ? null
                                : Number(note.replied_note_id)

                        if (
                            parentId !== null &&
                            ids.has(parentId) &&
                            !ids.has(Number(note.id))
                        ) {
                            ids.add(Number(note.id))
                            foundNew = true
                        }
                    })
                }

                return ids
            }

            const removeNotes = (offer: Offer | null | undefined) => {
                if (!offer?.offerNotes?.length) {
                    return
                }

                const ids = noteIdsToRemove(offer.offerNotes, action.payload.note_id)
                offer.offerNotes = offer.offerNotes.filter((note) => !ids.has(Number(note.id)))
            }

            removeNotes(state.all.find((o) => o.id === action.payload.offer_id))
            removeNotes(
                state.selectedOffer?.id === action.payload.offer_id ? state.selectedOffer : null
            )
        },        resetState: () => initialState,    },
})

export const {
    setOffers,
    pushOffer,
    pushCalculationToOffer,
    deleteOffer,
    setSelectedOffer,
    clearSelectedOfferReservation,
	setSelectedOfferPackagesCart,
	setSelectedOfferCalculationPackagesCart,
    resetSelectedOffer,
    setDraftOfferId,
    clearOffers,
    setOffersTableUiState,
    resetDefaultOffersTableValues,
    setOffersTableMeta,
    setPackageCartToOffer,
    mergePackagesCartResponse,
    pushOfferEmailTemplate,
    deleteOfferEmailTemplate,
    setOfferEmailTemplates,
    pushOfferNote,
    deleteOfferNote,
    resetState,
} = offersSlice.actions

export default offersSlice.reducer
